"use client";

import { motion } from "framer-motion";
import { UtensilsCrossed, Wine, Clock, Users, Calendar, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const experiences = [
  {
    name: "Dégustation Découverte",
    duration: "30 min",
    price: "Gratuit",
    persons: "1–4 personnes",
    desc: "Initiation aux saveurs de nos quatre gammes. Trois produits par gamme, présentés par nos hôtes. Idéal pour découvrir l'univers PATATE OR.",
    items: ["12 échantillons", "Présentation guidée", "Fiche recettes offerte"],
  },
  {
    name: "Dégustation Initiation",
    duration: "1h",
    price: "5 000 FCFA / pers.",
    persons: "1–8 personnes",
    desc: "Voyage sensoriel complet à travers nos quatre gammes. Accompagnement par un de nos chefs : accords, conseils culinaires, secrets de préparation.",
    items: ["24 échantillons", "Chef dédié", "Boissons artisanales", "Goodie bag"],
    featured: true,
  },
  {
    name: "Dégustation Prestige",
    duration: "2h",
    price: "15 000 FCFA / pers.",
    persons: "2–12 personnes",
    desc: "Expérience gastronomique d'exception. Menu dégustation 7 services à base de nos produits, accord vins et spiritueux (vodka de pomme de terre), table privatisée.",
    items: ["Menu 7 services", "Sommelier privé", "Table privatisée", "Cadeau signature"],
  },
];

const schedule = [
  { day: "Lundi – Vendredi", hours: "10h – 19h" },
  { day: "Samedi", hours: "09h – 21h" },
  { day: "Dimanche", hours: "10h – 14h" },
];

export function Degustation() {
  return (
    <section id="degustation" className="relative py-24 lg:py-32 overflow-hidden">
      <div className="absolute inset-0 -z-10">
        <img
          src="/images/degustation.png"
          alt="Dégustation au centre d'exposition PATATE OR"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background/95 via-background/88 to-background/95" />
      </div>

      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="divider-gold max-w-xs mx-auto mb-5"
          >
            <span className="text-xs uppercase tracking-[0.32em] text-gold-deep font-semibold">
              Dégustation · Centre d&apos;Exposition
            </span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.05 }}
            className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight"
          >
            Le goût à sa <span className="text-gradient-gold">source</span>
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="mt-6 text-base sm:text-lg text-foreground/75 leading-relaxed"
          >
            Au cœur de notre Centre d&apos;Exposition de Ndiamsyl Peye, vivez une
            expérience sensorielle unique. Trois formules de dégustation pour explorer nos
            quatre gammes, du produit brut à l&apos;assiette gastronomique.
          </motion.p>
        </div>

        {/* Experiences grid */}
        <div className="mt-16 grid lg:grid-cols-3 gap-6">
          {experiences.map((e, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              className={`relative p-6 sm:p-8 rounded-3xl transition-all duration-500 overflow-hidden ${
                e.featured
                  ? "bg-gradient-earth text-cream shadow-gold-lg ring-2 ring-gold/40 lg:-translate-y-4"
                  : "bg-card border border-border hover:border-gold hover:shadow-gold text-foreground"
              }`}
            >
              {e.featured && (
                <div className="absolute top-4 right-4">
                  <Badge className="bg-gradient-gold text-white shadow-gold border-0">
                    <Sparkles className="w-3 h-3 mr-1" />
                    Plus populaire
                  </Badge>
                </div>
              )}

              <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold mb-4 ${
                e.featured ? "bg-cream/15 text-cream" : "bg-secondary text-gold-deep"
              }`}>
                <UtensilsCrossed className="w-3.5 h-3.5" />
                {e.name}
              </div>

              <div className="flex items-baseline gap-2 mb-2">
                <Clock className={`w-4 h-4 ${e.featured ? "text-gold" : "text-gold-deep"}`} />
                <span className={`text-sm font-medium ${e.featured ? "text-cream/85" : "text-foreground/80"}`}>
                  {e.duration}
                </span>
              </div>
              <div className="flex items-baseline gap-2 mb-4">
                <Users className={`w-4 h-4 ${e.featured ? "text-gold" : "text-gold-deep"}`} />
                <span className={`text-sm font-medium ${e.featured ? "text-cream/85" : "text-foreground/80"}`}>
                  {e.persons}
                </span>
              </div>

              <p className={`text-sm leading-relaxed mb-5 ${e.featured ? "text-cream/80" : "text-muted-foreground"}`}>
                {e.desc}
              </p>

              <ul className="space-y-2 mb-6">
                {e.items.map((item, j) => (
                  <li key={j} className={`flex items-center gap-2 text-sm ${e.featured ? "text-cream/90" : "text-foreground/80"}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${e.featured ? "bg-gold" : "bg-gradient-gold"}`} />
                    {item}
                  </li>
                ))}
              </ul>

              <div className={`flex items-baseline justify-between pt-4 border-t ${e.featured ? "border-cream/15" : "border-border"}`}>
                <div>
                  <div className="text-[10px] uppercase tracking-wider opacity-70">Tarif</div>
                  <div className={`font-display text-xl font-extrabold ${e.featured ? "text-gold" : "text-gradient-gold"}`}>
                    {e.price}
                  </div>
                </div>
                <Button
                  onClick={() =>
                    document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" })
                  }
                  className={
                    e.featured
                      ? "bg-gradient-gold text-white shadow-gold hover:shadow-gold-lg rounded-full"
                      : "bg-card border border-gold text-foreground hover:bg-secondary rounded-full"
                  }
                >
                  <Calendar className="w-4 h-4 mr-1" />
                  Réserver
                </Button>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Schedule + CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="mt-14 grid lg:grid-cols-12 gap-6 items-stretch"
        >
          {/* Schedule */}
          <div className="lg:col-span-7 p-6 sm:p-8 rounded-3xl bg-card border border-border">
            <div className="flex items-center gap-3 mb-5">
              <div className="grid place-items-center w-12 h-12 rounded-xl bg-gradient-gold shadow-gold">
                <Wine className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-display text-2xl font-bold text-foreground">
                  Horaires des dégustations
                </h3>
                <p className="text-sm text-muted-foreground">
                  Centre d&apos;Exposition de Ndiamsyl Peye, Smartest Capital
                </p>
              </div>
            </div>

            <ul className="space-y-3">
              {schedule.map((s, i) => (
                <li key={i} className="flex items-center justify-between py-3 border-b border-border last:border-0">
                  <span className="text-sm font-medium text-foreground">{s.day}</span>
                  <span className="font-display text-base font-bold text-gradient-gold">{s.hours}</span>
                </li>
              ))}
            </ul>

            <p className="mt-5 text-xs text-muted-foreground">
              ⚠ Sur rendez-vous uniquement pour la Dégustation Prestige. Groups &amp; entreprises bienvenus.
            </p>
          </div>

          {/* CTA card */}
          <div className="lg:col-span-5 p-6 sm:p-8 rounded-3xl bg-gradient-gold text-white shadow-gold-lg flex flex-col justify-center">
            <Sparkles className="w-10 h-10 mb-3" />
            <h3 className="font-display text-2xl font-bold">
              Offrez une dégustation PATATE OR
            </h3>
            <p className="mt-2 text-white/90 text-sm leading-relaxed">
              Cartes cadeaux disponibles pour tous nos formats. Pour particuliers,
              entreprises, événements. Une expérience mémorable, 100% racines.
            </p>
            <Button
              onClick={() =>
                document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" })
              }
              className="mt-5 bg-white text-earth-deep hover:bg-cream rounded-full font-semibold self-start"
            >
              Offrir une carte cadeau
            </Button>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
